import { useLayoutEffect, useState } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5themes_Dark from "@amcharts/amcharts5/themes/Dark";
import _ from 'lodash';
import { priceFormatterV3 } from '../../settings/themeSettings';

function BarChart({ identifier, data = [], title = "Graphique", series = [] }) {
  const [selectedData, setSelectedData] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useLayoutEffect(() => {
    let root = am5.Root.new(identifier);

    // Style Web 3.0 moderne avec thème sombre et animé
    root.setThemes([
      am5themes_Animated.new(root),
      am5themes_Dark.new(root)
    ]);

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        pinchZoomX: false,
        layout: root.verticalLayout,
        paddingLeft: 20, // Plus d'espace pour les labels rotatés
        paddingBottom: 60, // Plus d'espace en bas pour les labels rotatés
        background: am5.Rectangle.new(root, {
          fill: am5.color("#0f0f23"),
          fillOpacity: 0.95
        })
      })
    );

    // Préparer les données selon le format reçu
    var _data = [];

    if (data.length > 0) {
      // Si les données sont déjà au bon format (avec category, value1, etc.), les utiliser directement
      if (data[0].category) {
        _data = data;
      } else {
        // Format billetterie (legacy)
        _data.push({
          category: "Billeterie",
          value1: _.sumBy(data, (d) => d.tarif),
          value2: _.sumBy(data, (d) => d.marge > 0 ? d.marge : 0)
        });
      }
    }

    // Create Y-axis avec style moderne
    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          strokeOpacity: 0.3,
          strokeWidth: 2,
          stroke: am5.color("#64ffda")
        }),
        numberFormatter: am5.NumberFormatter.new(root, {
          numberFormat: "#,###"
        })
      })
    );

    // Create X-Axis avec style moderne et meilleur affichage des labels
    let xRenderer = am5xy.AxisRendererX.new(root, {
      strokeOpacity: 0.3,
      strokeWidth: 2,
      stroke: am5.color("#64ffda"),
      cellStartLocation: 0.1,
      cellEndLocation: 0.9,
      minGridDistance: 30 // Distance minimale entre les labels
    });

    // Configuration des labels pour meilleure lisibilité
    xRenderer.labels.template.setAll({
      rotation: -45, // Rotation des labels pour éviter le chevauchement
      centerY: am5.p50,
      centerX: am5.p100,
      fontSize: "12px",
      fontWeight: "500",
      fill: am5.color("#ffffff"),
      oversizedBehavior: "wrap", // Permet le retour à la ligne
      maxWidth: 100, // Largeur maximale des labels
      textAlign: "center"
    });

    // Ajuster la distance minimale selon le nombre de catégories
    if (_data.length > 10) {
      xRenderer.set("minGridDistance", 80); // Plus d'espace pour beaucoup de données
    } else if (_data.length > 5) {
      xRenderer.set("minGridDistance", 60);
    } else {
      xRenderer.set("minGridDistance", 30);
    }

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        renderer: xRenderer,
        categoryField: "category"
      })
    );
    xAxis.data.setAll(_data);

    // Couleurs Web 3.0 modernes avec gradients
    const colors = [
      am5.color("#00d4ff"), // Cyan néon
      am5.color("#ff3d71"), // Rose néon
      am5.color("#00ff88"), // Vert néon
      am5.color("#ffa726"), // Orange néon
      am5.color("#ab47bc"), // Violet néon
    ];

    // Create series dynamically based on data avec effets Web 3.0
    const seriesConfigs = [
      { name: "Chiffre d'Affaires", field: "value", color: colors[0] },
      { name: "Marge", field: "value2", color: colors[1] },
      { name: "Montant Prévisionnel", field: "value1", color: colors[2] },
      { name: "Ecart", field: "value3", color: colors[3] }
    ];

    seriesConfigs.forEach((config, index) => {
      // Vérifier si cette série a des données
      const hasData = _data.some(item => item[config.field] && item[config.field] > 0);

      if (hasData) {
        let series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: config.name,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: config.field,
            categoryXField: "category",
            clustered: true, // Permet d'avoir des colonnes groupées
            tooltip: am5.Tooltip.new(root, {
              labelText: "{categoryX}: {valueY}",
              getFillFromSprite: false,
              fill: am5.color("#1a1a2e"),
              stroke: am5.color(config.color)
            })
          })
        );

        // Style Web 3.0 avec gradients et glow
        series.columns.template.setAll({
          fill: am5.LinearGradient.new(root, {
            stops: [
              { color: config.color, opacity: 1 },
              { color: am5.color("#000000"), opacity: 0.3 }
            ]
          }),
          stroke: config.color,
          strokeWidth: 2,
          cornerRadiusTL: 8,
          cornerRadiusTR: 8,
          fillOpacity: 0.8,
          strokeOpacity: 1,
          cursorOverStyle: "pointer",
          width: am5.percent(80), // Largeur des colonnes à 80%
          maxWidth: 50 // Largeur maximale des colonnes
        });

        // Effets hover Web 3.0
        series.columns.template.states.create("hover", {
          fillOpacity: 1,
          strokeWidth: 3,
          shadowColor: config.color,
          shadowBlur: 15,
          shadowOffsetX: 0,
          shadowOffsetY: 0
        });

        // Gestionnaire de clic pour popup
        series.columns.template.on("click", function (ev) {
          const dataContext = ev.target.dataItem.dataContext;
          setSelectedData({
            category: dataContext.category,
            series: config.name,
            value: priceFormatterV3(dataContext[config.field]),
            rawValue: dataContext[config.field]
          });
          setShowModal(true);
        });

        series.data.setAll(_data);
      }
    });

    // Legend moderne avec style Web 3.0
    let legend = chart.children.push(am5.Legend.new(root, {
      centerX: am5.p50,
      x: am5.p50,
      marginTop: 15,
      marginBottom: 15
    }));

    legend.labels.template.setAll({
      fontSize: "14px",
      fontWeight: "500",
      fill: am5.color("#ffffff")
    });

    legend.valueLabels.template.setAll({
      fontSize: "12px",
      fill: am5.color("#64ffda")
    });

    legend.data.setAll(chart.series.values);

    // Curseur moderne avec zoom
    let cursor = chart.set("cursor", am5xy.XYCursor.new(root, {
      behavior: "none"
    }));

    cursor.lineY.set("visible", false);
    cursor.lineX.setAll({
      stroke: am5.color("#64ffda"),
      strokeWidth: 2,
      strokeOpacity: 0.7
    });

    // Bouton de zoom reset moderne
    let zoomOut = chart.plotContainer.children.push(am5.Button.new(root, {
      label: am5.Label.new(root, {
        text: "Zoom Reset",
        fontSize: "12px",
        fill: am5.color("#ffffff")
      }),
      x: am5.p100,
      centerX: am5.p100,
      y: 0,
      marginRight: 20,
      marginTop: 20
    }));

    zoomOut.get("background").setAll({
      fill: am5.color("#64ffda"),
      fillOpacity: 0.2,
      stroke: am5.color("#64ffda"),
      strokeWidth: 2,
      cornerRadius: 8
    });

    zoomOut.on("click", function () {
      xAxis.zoom(0, 1);
    });

    // Zoom initial adaptatif pour les graphiques avec beaucoup de données
    if (_data.length > 8) {
      // Zoom initial pour afficher environ 8 catégories à la fois
      chart.appear(1000, 100).then(() => {
        xAxis.zoom(0, Math.min(1, 8 / _data.length));
      });
    } else {
      // Animation standard pour peu de données
      chart.appear(1000, 100);
    }

    return () => {
      root.dispose();
    };
  }, [data, identifier]);

  // Modal popup moderne Web 3.0
  const Modal = () => (
    showModal && selectedData && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(15, 15, 35, 0.9)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
      }} onClick={() => setShowModal(false)}>
        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
          border: '2px solid #64ffda',
          borderRadius: '20px',
          padding: '40px',
          boxShadow: '0 20px 60px rgba(100, 255, 218, 0.3)',
          color: '#ffffff',
          textAlign: 'center',
          minWidth: '400px',
          animation: 'modalSlideIn 0.3s ease-out'
        }} onClick={(e) => e.stopPropagation()}>
          <h2 style={{
            color: '#64ffda',
            marginBottom: '20px',
            fontSize: '1.8rem',
            textShadow: '0 0 20px rgba(100, 255, 218, 0.5)'
          }}>
            Détails - {selectedData.category}
          </h2>
          <p style={{ fontSize: '1.2rem', marginBottom: '10px' }}>
            <strong style={{ color: '#00ff88' }}>{selectedData.series}:</strong>
          </p>
          <p style={{
            fontSize: '2rem',
            color: '#00d4ff',
            fontWeight: 'bold',
            textShadow: '0 0 15px rgba(0, 212, 255, 0.5)',
            marginBottom: '30px'
          }}>
            {selectedData.value}
          </p>
          <button
            onClick={() => setShowModal(false)}
            style={{
              background: 'linear-gradient(135deg, #64ffda, #00d4ff)',
              border: 'none',
              borderRadius: '25px',
              padding: '12px 30px',
              color: '#1a1a2e',
              fontWeight: 'bold',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
          >
            Fermer
          </button>
        </div>
      </div>
    )
  );

  return (
    <>
      <div id={identifier} style={{ width: "100%", height: "100%" }}></div>
      <Modal />
      <style>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </>
  );
}
export default BarChart;
